/**
 * Dictation in the message box (TMMAPP-0142 item 4a, Phase 1): the browser's own speech recognition
 * (`SpeechRecognition` / `webkitSpeechRecognition`, no library) in the app's language. Interim words
 * are reported as they change and final words once each phrase settles; the caller puts them in the
 * box. Nothing is ever sent from here. Where the API is missing, `supported` is false and the
 * caller hides the button (the device's own dictation still works in the text box).
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/** The slice of the Web Speech API this hook uses (not in TypeScript's DOM library). */
interface RecognitionAlternative {
  transcript: string;
}
interface RecognitionResult {
  readonly isFinal: boolean;
  readonly length: number;
  [index: number]: RecognitionAlternative;
}
interface RecognitionResultList {
  readonly length: number;
  [index: number]: RecognitionResult;
}
export interface RecognitionResultEvent {
  resultIndex: number;
  results: RecognitionResultList;
}
export interface RecognitionErrorEvent {
  error: string;
}
export interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: RecognitionResultEvent) => void) | null;
  onerror: ((event: RecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}
type RecognitionConstructor = new () => SpeechRecognitionLike;

/** The browser's recognition constructor, or null where the API is missing. */
export function recognitionConstructor(): RecognitionConstructor | null {
  if (typeof window === 'undefined') return null;
  const scope = window as unknown as {
    SpeechRecognition?: RecognitionConstructor;
    webkitSpeechRecognition?: RecognitionConstructor;
  };
  return scope.SpeechRecognition ?? scope.webkitSpeechRecognition ?? null;
}

/** The recognizer's language for the app's locale (`en-US`, `es`, `fr`, `pt-BR`). */
export function recognitionLanguage(languageTag: string): string {
  return languageTag === 'en' ? 'en-US' : languageTag;
}

const DENIED: ReadonlySet<string> = new Set(['not-allowed', 'service-not-allowed']);

export interface DictationHandlers {
  /** The phrase being heard, replaced on each change; '' when it settles or stops. */
  onInterim: (text: string) => void;
  /** A settled phrase, to insert. */
  onFinal: (text: string) => void;
  /** The browser ended recognition on its own (silence, an error, its own limit). */
  onEnd: () => void;
}

export interface Dictation {
  supported: boolean;
  listening: boolean;
  /** The microphone was refused (the browser's or the person's setting). */
  denied: boolean;
  /** Starts listening; false when it couldn't (no API, already listening, or the browser refused). */
  start: () => boolean;
  stop: () => void;
}

export function useDictation(languageTag: string, handlers: DictationHandlers): Dictation {
  const [supported] = useState(() => recognitionConstructor() !== null);
  const [listening, setListening] = useState(false);
  const [denied, setDenied] = useState(false);
  const recognition = useRef<SpeechRecognitionLike | null>(null);
  const handlersRef = useRef(handlers);
  useEffect(() => {
    handlersRef.current = handlers;
  });

  const stop = useCallback(() => {
    const current = recognition.current;
    if (current === null) return;
    recognition.current = null;
    current.onresult = null;
    current.onerror = null;
    current.onend = null;
    current.stop();
    handlersRef.current.onInterim('');
    setListening(false);
  }, []);

  const start = useCallback((): boolean => {
    const Ctor = recognitionConstructor();
    if (Ctor === null || recognition.current !== null) return false;
    const next = new Ctor();
    next.lang = recognitionLanguage(languageTag);
    next.continuous = true;
    next.interimResults = true;
    next.onresult = (event) => {
      let interim = '';
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];
        const words = result?.[0]?.transcript ?? '';
        if (result?.isFinal) handlersRef.current.onFinal(words);
        else interim += words;
      }
      handlersRef.current.onInterim(interim);
    };
    next.onerror = (event) => {
      if (DENIED.has(event.error)) setDenied(true);
    };
    // The browser ended recognition (silence, an error, or its own limit).
    next.onend = () => {
      if (recognition.current !== next) return;
      recognition.current = null;
      setListening(false);
      handlersRef.current.onEnd();
    };
    recognition.current = next;
    setDenied(false);
    setListening(true);
    try {
      next.start();
      return true;
    } catch {
      recognition.current = null;
      setListening(false);
      return false;
    }
  }, [languageTag]);

  // Leaving the page stops listening and releases the microphone.
  useEffect(
    () => () => {
      const current = recognition.current;
      recognition.current = null;
      if (current !== null) {
        current.onresult = null;
        current.onerror = null;
        current.onend = null;
        current.abort();
      }
    },
    [],
  );

  return { supported, listening, denied, start, stop };
}
